import { useCreatePortalSession } from '@/features/billing/api/create-portal-session';
import { ROUTES } from '@/lib/route-paths';
import { useSession } from '@/lib/session';
import { webAppUrl } from '@/lib/web-app-origin';
import type { BillingIssue } from '@borradh-workspace/contracts';
import { Link } from '@tanstack/react-router';
import { AlertCircle } from 'lucide-react';

export function BillingIssueNotice({
  issue,
  showLink = true,
  compact = false,
}: {
  issue: BillingIssue;
  showLink?: boolean;
  compact?: boolean;
}) {
  if (compact) {
    return (
      <div role="status" className="flex items-start gap-3 text-sm">
        <AlertCircle
          aria-hidden="true"
          className="mt-0.5 size-5 shrink-0 text-amber-600"
        />
        <div className="min-w-0 flex-1">
          <p className="font-semibold">{issue.title}</p>
          {issue.paymentFailureReason ? (
            <p className="text-muted-foreground">
              {issue.paymentFailureReason}
            </p>
          ) : null}
          {showLink ? <ManagePaymentsAction issue={issue} /> : null}
        </div>
      </div>
    );
  }

  const awaitingReason = ['past_due', 'unpaid', 'incomplete'].includes(
    issue.status
  );
  return (
    <div role="status" className="flex items-start gap-3">
      <div className="space-y-2 text-sm">
        <p className="font-semibold">{issue.title}</p>
        <p className="text-muted-foreground">{issue.message}</p>
        {issue.paymentFailureReason ? (
          <p>{issue.paymentFailureReason}</p>
        ) : awaitingReason ? (
          <p className="text-muted-foreground">
            A specific payment failure reason is not available. Review the
            payment in Billing or contact your bank for details.
          </p>
        ) : null}
        {showLink ? <ManagePaymentsAction issue={issue} /> : null}
      </div>
    </div>
  );
}

function ManagePaymentsAction({ issue }: { issue: BillingIssue }) {
  const { data: session } = useSession();
  const { openPortal, isOpening } = useCreatePortalSession();
  if (!['owner', 'admin'].includes(session?.user?.orgRole ?? '')) {
    return (
      <p className="text-muted-foreground">
        Ask your owner or admin to manage payments.
      </p>
    );
  }
  const className = 'font-medium underline underline-offset-4';
  if (!['past_due', 'unpaid', 'incomplete'].includes(issue.status)) {
    return (
      <Link to={ROUTES.settingsBilling} className={className}>
        Manage payments
      </Link>
    );
  }
  return (
    <button
      type="button"
      className={className}
      disabled={isOpening}
      onClick={() =>
        openPortal({
          returnUrl: webAppUrl(
            window.location.pathname + window.location.search
          ),
          flow: 'payment_recovery',
        })
      }
    >
      {isOpening ? 'Opening…' : 'Manage payments'}
    </button>
  );
}
